import { useEffect, useRef } from 'react'
import { useTheme } from '@/hooks/useTheme'
import {
  MimirBrain,
  type MimirBrainInstance,
  type PosPalette,
} from './engines/brain'

export type Pos =
  | 'noun'
  | 'verb'
  | 'adjective'
  | 'adverb'
  | 'pronoun'
  | 'preposition'
  | 'determiner'
  | 'conjunction'

export interface BrainNodeInput {
  pos: Pos
  /** Familiarity 0..1. Drives glow/edge weight. Synthetic when absent. */
  strength?: number
}

interface VocabularyBrainProps {
  /** Real known words. Absent → engine generates a synthetic field of `count` nodes. */
  nodes?: BrainNodeInput[]
  count?: number
  autoSpeed?: number
  posPalette?: PosPalette
  /** 0..1 — how tightly each part of speech gathers toward its region. */
  clusterPull?: number
  /** Screen distance in px under which two words are joined by an edge. */
  linkDistance?: number
  /** Called with the rendered node count so consumers can show "<n> words known". */
  onCount?: (count: number) => void
  className?: string
}

/**
 * The rotating 3D vocabulary brain. Nodes are coloured by part of speech and
 * clustered into anatomical regions (engine `DEFAULT_POS`). The engine honours
 * reduced-motion internally (no auto-yaw; drag still works), so it always mounts.
 * Recreates with the active palette on theme change; tears down on unmount.
 *
 * FR-009 hook point: when real spaced-repetition data exists, per-node
 * `strength` from `nodes` will drive glow/edge once the engine accepts an
 * explicit node set. Today `nodes` only sizes the field (count); the engine
 * fills strength with a synthetic Gaussian.
 */
export function VocabularyBrain({
  nodes,
  count,
  autoSpeed,
  posPalette,
  clusterPull,
  linkDistance,
  onCount,
  className,
}: VocabularyBrainProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const onCountRef = useRef(onCount)
  onCountRef.current = onCount
  // Theme is read so the effect re-runs (and the engine recolours) on toggle.
  const { dark } = useTheme()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handle: MimirBrainInstance = MimirBrain(canvas, {
      count: nodes?.length ?? count,
      pos: posPalette,
      autoSpeed,
      dark,
      clusterPull,
      linkDistance,
    })
    onCountRef.current?.(handle.getParticles().length)

    return () => handle.destroy()
  }, [nodes, count, autoSpeed, posPalette, dark, clusterPull, linkDistance])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className ?? 'h-full w-full'}
    />
  )
}
